/* ==========================================================================
   kfactor.js
   Alpine component for the QS100 / QS200 K-factor calculator tool.

   Standalone asset, not part of the Vite-compiled main-*.js bundle — same
   reasoning as kfactor.css and chem-compat.js. Re-add after each theme
   update.

   Registers via Alpine.data(), so it works with x-data="kFactorCalc()" in
   any markup. No jQuery, no DOM query library — Alpine handles all
   reactivity directly.
   ========================================================================== */

// Shared, cached fetch — same pattern as window.ChemCompatData, so if
// multiple instances of this component ever end up on one page,
// kfactor-data.json is only ever downloaded once per session.
window.KFactorData = (function () {
  let promise = null;
  return {
    load(url) {
      if (!promise) {
        promise = fetch(url).then((r) => {
          if (!r.ok) throw new Error('kfactor-data.json request failed: ' + r.status);
          return r.json();
        });
      }
      return promise;
    },
  };
})();

document.addEventListener('alpine:init', () => {
  Alpine.data('kFactorCalc', (config = {}) => ({
    // ---- config ----
    dataUrl: config.dataUrl,

    // ---- state ----
    model: config.model || 'QS100', // 'QS100' | 'QS200'
    data: null,
    loading: true,
    error: null,

    pipeQuery: '',
    brandQuery: '',
    selectedPipe: null,
    selectedBrand: null,
    focusedPipeIndex: 0,
    focusedBrandIndex: 0,

    async init() {
      this.$watch('pipeQuery', () => { this.focusedPipeIndex = 0; });
      this.$watch('brandQuery', () => { this.focusedBrandIndex = 0; });
      try {
        this.data = await window.KFactorData.load(this.dataUrl);
      } catch (e) {
        this.error = 'Could not load K-factor data. Please refresh and try again.';
      } finally {
        this.loading = false;
      }
    },

    switchModel(newModel) {
      if (this.model === newModel) return;
      this.model = newModel;
      this.pipeQuery = '';
      this.brandQuery = '';
      this.selectedPipe = null;
      this.selectedBrand = null;
      this.focusedPipeIndex = 0;
      this.focusedBrandIndex = 0;
    },

    get dataset() {
      return this.data ? this.data[this.model] : null;
    },

    // ---- pipe size filter ----
    get pipeOptions() {
      if (!this.dataset) return [];
      const q = this.pipeQuery.trim().toLowerCase();
      return !q
        ? this.dataset.pipeOrder
        : this.dataset.pipeOrder.filter((p) => p.toLowerCase().includes(q));
    },

    isPipeAvailable(pipe) {
      if (!this.dataset) return false;
      return this.dataset.rows.some(
        (r) => r.pipe === pipe && (!this.selectedBrand || r.brand === this.selectedBrand)
      );
    },

    selectPipe(pipe) {
      if (this.selectedPipe === pipe) {
        this.selectedPipe = null;
      } else {
        if (!this.isPipeAvailable(pipe)) return; // guard, list already disables these
        this.selectedPipe = pipe;
      }
      // if the current brand selection has no rows for the new pipe, drop it
      // rather than silently showing zero results for a now-meaningless pair
      if (this.selectedBrand && !this.pairExists(this.selectedPipe, this.selectedBrand)) {
        this.selectedBrand = null;
      }
    },

    // ---- controller brand filter ----
    get brandOptions() {
      if (!this.dataset) return [];
      const q = this.brandQuery.trim().toLowerCase();
      return !q
        ? this.dataset.brandOrder
        : this.dataset.brandOrder.filter((b) => b.toLowerCase().includes(q));
    },

    isBrandAvailable(brand) {
      if (!this.dataset) return false;
      return this.dataset.rows.some(
        (r) => r.brand === brand && (!this.selectedPipe || r.pipe === this.selectedPipe)
      );
    },

    selectBrand(brand) {
      if (this.selectedBrand === brand) {
        this.selectedBrand = null;
      } else {
        if (!this.isBrandAvailable(brand)) return;
        this.selectedBrand = brand;
      }
      if (this.selectedPipe && !this.pairExists(this.selectedPipe, this.selectedBrand)) {
        this.selectedPipe = null;
      }
    },

    pairExists(pipe, brand) {
      if (!this.dataset) return false;
      return this.dataset.rows.some(
        (r) => (!pipe || r.pipe === pipe) && (!brand || r.brand === brand)
      );
    },

    clearAll() {
      this.selectedPipe = null;
      this.selectedBrand = null;
      this.pipeQuery = '';
      this.brandQuery = '';
      this.focusedPipeIndex = 0;
      this.focusedBrandIndex = 0;
    },

    get hasActiveFilters() {
      return !!(this.selectedPipe || this.selectedBrand);
    },

    // ---- results ----
    get results() {
      if (!this.dataset) return [];
      return this.dataset.rows.filter(
        (r) =>
          (!this.selectedPipe || r.pipe === this.selectedPipe) &&
          (!this.selectedBrand || r.brand === this.selectedBrand)
      );
    },

    get totalCount() {
      return this.dataset ? this.dataset.rows.length : 0;
    },

    formatK(k) {
      return typeof k === 'string' ? k : k.toString();
    },

    // ---- keyboard nav: model tablist (2 tabs, automatic activation) ----
    onModelKeydown(e) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const newModel = this.model === 'QS100' ? 'QS200' : 'QS100';
      this.switchModel(newModel);
      this.$nextTick(() => {
        const ref = newModel === 'QS100' ? 'tabQS100' : 'tabQS200';
        if (this.$refs[ref]) this.$refs[ref].focus();
      });
    },

    // ---- keyboard nav: pipe size listbox (roving tabindex) ----
    onPipeListKeydown(e) {
      const items = this.pipeOptions;
      if (!items.length) return;
      let idx = this.focusedPipeIndex;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); idx = Math.min(idx + 1, items.length - 1); break;
        case 'ArrowUp': e.preventDefault(); idx = Math.max(idx - 1, 0); break;
        case 'Home': e.preventDefault(); idx = 0; break;
        case 'End': e.preventDefault(); idx = items.length - 1; break;
        default: return;
      }
      this.focusedPipeIndex = idx;
      this.$nextTick(() => {
        const buttons = this.$refs.pipeList.querySelectorAll('.kfactor__list-item');
        if (buttons[idx]) buttons[idx].focus();
      });
    },

    // ---- keyboard nav: controller brand listbox (roving tabindex) ----
    onBrandListKeydown(e) {
      const items = this.brandOptions;
      if (!items.length) return;
      let idx = this.focusedBrandIndex;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); idx = Math.min(idx + 1, items.length - 1); break;
        case 'ArrowUp': e.preventDefault(); idx = Math.max(idx - 1, 0); break;
        case 'Home': e.preventDefault(); idx = 0; break;
        case 'End': e.preventDefault(); idx = items.length - 1; break;
        default: return;
      }
      this.focusedBrandIndex = idx;
      this.$nextTick(() => {
        const buttons = this.$refs.brandList.querySelectorAll('.kfactor__list-item');
        if (buttons[idx]) buttons[idx].focus();
      });
    },
  }));
});
